"use client";

import clsx from "clsx";
import { Flourish, flourishes } from "@/types/flourish";
import styles from "./FaqBlock.module.scss";
import { Faq, FaqCategory, FaqQueryResult } from "../../../sanity/types";
import { useId, useState } from "react";
import { ALL_CATEGORY } from "@/utilities/constants";
import { FaqAccordion } from "../../shared/FaqAccordion/FaqAccordion";
import { PortableTextRichText } from "../../shared/PortableTextRichText/PortableTextRichText";
import Image from "next/image";
import { RadioGroupField } from "../../form/fields/RadioGroupField/RadioGroupField";
import { RadioInput } from "../../form/input/RadioInput/RadioInput";

interface FaqBlockProps {
	title?: string | null;
	flourish?: Flourish | null;
	body?: string | null;
	categories?: FaqCategory[] | null;
	items?: FaqQueryResult | null;
	theme?: "dawn" | "dusk" | "midnight" | null;
}

export const FaqBlock = ({ title, flourish, body, categories, items, theme }: FaqBlockProps) => {
	const flourishImage = flourish && flourish in flourishes && flourishes[flourish];
	const id = useId();
	const shouldShowAll = categories && categories?.length > 1;
	const defaultCategory = shouldShowAll ? ALL_CATEGORY : categories ? categories[0]?._id : null;
	const [activeCategory, setActiveCategory] = useState<string | null>(defaultCategory);

	let radioCategories: Array<{ title?: string; value: string }> = [];
	if (shouldShowAll) {
		radioCategories.push({ title: "All", value: ALL_CATEGORY });
	}
	if (categories) {
		radioCategories = radioCategories.concat(
			categories.map(category => ({ title: category.title, value: category._id })),
		);
	}

	const filterAccordionItems = (activeCategory: string) => {
		if (!items) return [];

		return items.filter(item => {
			if (activeCategory === ALL_CATEGORY) {
				return true;
			}

			return item.category?._ref === activeCategory;
		});
	};

	const filteredCategories = radioCategories?.filter(category => {
		if (category.value === ALL_CATEGORY) {
			return true;
		}

		return items?.some(item => item.category?._ref === category.value);
	});

	const filteredItems = activeCategory ? filterAccordionItems(activeCategory) : [];

	return (
		<section className={clsx(styles["faq-block"], styles[`faq-block--${theme}`])}>
			<div className={clsx(styles["faq-block__wrapper"], "grid-cols-12")}>
				<div
					className={clsx(
						styles["faq-block__inner"],
						"col-full col-start-md-2 col-md-10 col-start-lg-3 col-lg-8",
					)}
				>
					<div className={styles["faq-block__content"]}>
						{(title || flourish) && (
							<div className={styles["faq-block__header"]}>
								{title && <h2 className={clsx(styles["faq-block__heading"])}>{title}</h2>}
								{flourishImage && (
									<Image
										alt={flourishImage.alt}
										className={styles["faq-block__flourish"]}
										height={flourishImage.height}
										src={`/${flourishImage.src}`}
										width={flourishImage.width}
									/>
								)}
							</div>
						)}
						{body && <p className={styles["faq-block__body"]}>{body}</p>}
					</div>

					{/* <RadioGroupField
						label="Filter by category"
						hideLabel
						name={`faq-block-${id}`}
						options={radioCategories.map(category => ({
							value: category.value,
							label: category.title || "",
						}))}

					/> */}

					<div
						className={styles["faq-block__filter"]}
						role="radiogroup"
						aria-labelledby={"faq-block-radio-legend-" + id}
					>
						<span
							className="sr-only"
							id={"faq-block-radio-legend-" + id}
						>
							Filter FAQs by category:
						</span>
						{filteredCategories?.map((category, index) => (
							<RadioInput
								key={category.value}
								label={category.title || ""}
								value={category.value}
								role="radio"
								name={`faq-block-${id}`}
								inputClassName={styles["faq-block__radio-input"]}
								labelClassName={styles["faq-block__radio-label"]}
								checked={activeCategory === category.value}
								onChange={() => setActiveCategory(category.value)}
							/>
						))}
					</div>

					<ul className={styles["faq-block__list"]}>
						{filteredItems.map((item, index) => (
							<li key={item._id}>
								<FaqAccordion
									title={item.title}
									content={item.content}
								/>
							</li>
						))}
					</ul>
				</div>
			</div>
		</section>
	);
};
